import { useEffect, useState } from "react";
import { Cookie } from "lucide-react";
import CookiePreferencesModal from "@/components/CookiePreferencesModal";
import { getStoredConsent, fetchConsentConfig } from "@/lib/consent";

/**
 * Persistent floating pill that lets a user re-open the cookie preferences editor
 * from any page. Shown only *after* an initial decision has been recorded — before
 * that, the main CookieBanner is already on-screen and clutter would be excessive.
 *
 * Sept 2026: the two chat bubbles that used to occupy the bottom-right corner
 * (legacy ChatWidget + KimaniFAB) were removed — this is now the bottom-most
 * bubble in that stack, with FeedbackWidget directly above it.
 */
export default function CookiePreferencesLauncher() {
    const [open, setOpen] = useState(false);
    const [hasDecision, setHasDecision] = useState(() => !!getStoredConsent());
    const [cfg, setCfg] = useState({ region: "OTHER", ccpa_visible: false });

    useEffect(() => {
        // Keep in sync if the main banner records the first decision this session.
        const onChange = () => setHasDecision(!!getStoredConsent());
        window.addEventListener("md-consent-changed", onChange);
        window.addEventListener("md-open-cookie-preferences", () => setOpen(true));
        // Region hint (best-effort, only used to decide if we should surface CCPA link)
        fetchConsentConfig().then(setCfg);
        return () => {
            window.removeEventListener("md-consent-changed", onChange);
        };
    }, []);

    if (!hasDecision) return null;

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                aria-label="Open cookie preferences"
                data-testid="cookie-preferences-launcher"
                className="fixed bottom-20 md:bottom-5 right-5 z-[9998] inline-flex items-center gap-1.5 rounded-full border border-border bg-card/95 backdrop-blur px-3 py-2 text-xs font-bold text-foreground shadow-lg hover:border-primary hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
                <Cookie className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Cookie preferences</span>
            </button>
            <CookiePreferencesModal
                open={open}
                onClose={() => setOpen(false)}
                region={cfg.region}
                ccpaVisible={cfg.ccpa_visible}
            />
        </>
    );
}
